import { initiateWsSession } from '@/services/api';
import io, { Socket } from 'socket.io-client';

const storageKey = (namespace: string) => `ws-session:${namespace}`;
const TAB_OWNER_STORAGE_KEY = 'ws-tab-owner';
const TAB_OWNER_CHANNEL = 'ws-tab-owner';
const TAB_OWNERSHIP_CLAIM_TIMEOUT_MS = 100;

const pendingSessions = new Map<string, Promise<string>>();

let tabOwnerChannel: BroadcastChannel | undefined;
const ownershipClaims = new Map<string, () => void>();

const WS_SESSION_TTL_MS = 90 * 60 * 1000;

interface CachedSession {
  session_id: string;
  expires_at: number;
}

const createTabOwnerId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getTabOwnerId = () => {
  const storedOwnerId = sessionStorage.getItem(TAB_OWNER_STORAGE_KEY);
  if (storedOwnerId) return storedOwnerId;

  const ownerId = createTabOwnerId();
  sessionStorage.setItem(TAB_OWNER_STORAGE_KEY, ownerId);
  return ownerId;
};

export function clearAllWsSessions(): void {
  const cachedSessionKeys: string[] = [];

  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith('ws-session:')) {
      cachedSessionKeys.push(key);
    }
  }

  cachedSessionKeys.forEach((key) => sessionStorage.removeItem(key));
}

const getTabOwnerChannel = () => {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
    return undefined;
  }

  if (!tabOwnerChannel) {
    tabOwnerChannel = new window.BroadcastChannel(TAB_OWNER_CHANNEL);
    tabOwnerChannel.onmessage = (event: MessageEvent) => {
      const data = event.data;

      if (data?.type === 'claim' && data.ownerId === getTabOwnerId()) {
        tabOwnerChannel?.postMessage({ type: 'owner-active', claimId: data.claimId });
      }

      if (data?.type === 'owner-active' && typeof data.claimId === 'string') {
        ownershipClaims.get(data.claimId)?.();
      }
    };
  }

  return tabOwnerChannel;
};

export const claimWsTabOwnership = (): Promise<boolean> => {
  const ownerId = getTabOwnerId();
  const channel = getTabOwnerChannel();
  if (!channel) return Promise.resolve(false);

  const claimId = createTabOwnerId();

  return new Promise((resolve) => {
    let completed = false;
    let timer: ReturnType<typeof setTimeout>;

    const finish = (isDuplicateTab: boolean) => {
      if (completed) return;

      completed = true;
      clearTimeout(timer);
      ownershipClaims.delete(claimId);

      if (isDuplicateTab) {
        clearAllWsSessions();
        sessionStorage.setItem(TAB_OWNER_STORAGE_KEY, createTabOwnerId());
      }

      resolve(isDuplicateTab);
    };

    ownershipClaims.set(claimId, () => finish(true));
    timer = setTimeout(() => finish(false), TAB_OWNERSHIP_CLAIM_TIMEOUT_MS);
    channel.postMessage({ type: 'claim', ownerId, claimId });
  });
};

function readCachedSession(namespace: string): string | null {
  const raw = sessionStorage.getItem(storageKey(namespace));
  if (!raw) return null;

  if (raw.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'session_id' in parsed &&
        'expires_at' in parsed &&
        typeof (parsed as CachedSession).session_id === 'string' &&
        typeof (parsed as CachedSession).expires_at === 'number'
      ) {
        const cached = parsed as CachedSession;
        if (cached.expires_at > Date.now()) {
          return cached.session_id;
        }
      }
    } catch {}
  }

  return null;
}

function storeSession(namespace: string, sessionId: string): void {
  sessionStorage.setItem(
    storageKey(namespace),
    JSON.stringify({ session_id: sessionId, expires_at: Date.now() + WS_SESSION_TTL_MS }),
  );
}

export async function getOrCreateWsSession(
  namespace: string,
  createSession = initiateWsSession,
): Promise<string> {
  const cached = readCachedSession(namespace);
  if (cached) return cached;

  let promise = pendingSessions.get(namespace);
  if (!promise) {
    promise = (async () => {
      try {
        const response = await createSession(namespace);
        const sessionId = response.session_id;
        storeSession(namespace, sessionId);
        return sessionId;
      } finally {
        pendingSessions.delete(namespace);
      }
    })();
    pendingSessions.set(namespace, promise);
  }
  return promise;
}

export async function getWsAuth(namespace: string): Promise<{ session_id: string }> {
  return { session_id: await getOrCreateWsSession(namespace) };
}

export async function refreshWsSession(
  namespace: string,
  createSession = initiateWsSession,
): Promise<string> {
  sessionStorage.removeItem(storageKey(namespace));
  return getOrCreateWsSession(namespace, createSession);
}

export function clearWsSession(namespace: string): void {
  sessionStorage.removeItem(storageKey(namespace));
}

export function createSocketClient(namespace: string): Socket {
  const socket = io(namespace, {
    autoConnect: false,
    path: '/api/ws/',
    transports: ['websocket'],
    auth: (cb: (data: object) => void) => {
      getWsAuth(namespace)
        .then((authData) => cb(authData))
        .catch((err: unknown) => {
          console.error('Failed to retrieve WS auth session:', err);
          cb({});
        });
    },
  });

  let sessionErrorRetried = false;

  socket.on('connect_error', (error: unknown) => {
    if (sessionErrorRetried) return;

    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes('Invalid or expired websocket session_id') ||
      message.includes('Missing websocket session_id') ||
      message.includes('Websocket session namespace mismatch')
    ) {
      sessionErrorRetried = true;
      clearWsSession(namespace);
      socket.connect();
    }
  });

  socket.on('connect', () => {
    sessionErrorRetried = false;
  });

  return socket;
}
